import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { StatusBadge } from "../../components/StatusBadge";
import { api, asList, formatError, mediaUrl } from "../../lib/api";
import { fmtDate } from "../../lib/content";
import { EditorDialog } from "./EditorDialog";
import { SCHEMAS } from "./schemas";

const PUBLIC_PATH = { products: "/products/", services: "/consulting", articles: "/insights/" };

export const ContentManager = ({ schemaKey }) => {
  const schema = SCHEMAS[schemaKey];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/${schemaKey}`).then((r) => setItems(asList(r.data))).catch((e) => toast.error(formatError(e)));
  }, [schemaKey]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/admin/${schemaKey}/${toDelete.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setToDelete(null);
  };

  const link = (it) => (schemaKey === "services" ? PUBLIC_PATH.services : `${PUBLIC_PATH[schemaKey]}${it.slug}`);

  return (
    <div data-testid={`manager-${schemaKey}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-teal/80">{items.length} {schema.label.toLowerCase()} entries</p>
        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid={`new-${schemaKey}-btn`} className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm text-mist hover:bg-teal-ink">
          <Plus className="h-4 w-4" /> New {schema.singular}
        </button>
      </div>
      <div className="mt-6 divide-y divide-teal/10 border-y border-teal/10 bg-white">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`${schemaKey}-row-${i}`} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded bg-mist">
              {it[schema.imageKey] && <img src={mediaUrl(it[schema.imageKey])} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-teal">{it[schema.titleKey]}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-teal/75">
                {it.status && <StatusBadge status={it.status} />}
                {it.category && <span>{it.category}</span>}
                <span className={`rounded-full px-2 py-0.5 ${it.published ? "bg-sage-light text-teal-ink" : "bg-stone text-teal-ink"}`}>{it.published ? "Published" : "Draft"}</span>
                <span>Updated {fmtDate(it.updated_at)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {it.published && (
                <Link to={link(it)} target="_blank" className="flex h-10 w-10 items-center justify-center rounded-full border border-teal/20 text-teal hover:bg-mist" aria-label="View on site" data-testid={`${schemaKey}-view-${i}`}>
                  <ExternalLink className="h-4 w-4" />
                </Link>
              )}
              <button onClick={() => { setEditing(it); setOpen(true); }} className="flex h-10 w-10 items-center justify-center rounded-full border border-teal/20 text-teal hover:bg-mist" aria-label="Edit" data-testid={`${schemaKey}-edit-${i}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => setToDelete(it)} className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-700 hover:bg-red-50" aria-label="Delete" data-testid={`${schemaKey}-delete-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-10 text-center text-sm text-teal/70">Nothing here yet. Create your first {schema.singular}.</p>}
      </div>
      <EditorDialog open={open} onOpenChange={setOpen} schemaKey={schemaKey} schema={schema} item={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-paper">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{toDelete?.[schema.titleKey]}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from the website permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
